import { Flex, FlexProps, Input, InputProps } from 'theme-ui';
import { useRef, useState } from 'react';

import useClickOutside from '../../hooks/useClickOutside';
import useDateIntervalFromMonth from '../../hooks/useDateIntervalFromMonth';
import useKeydown from '../../hooks/useKeydown';
import Calendar, { CalendarProps } from '../Calendar';

export interface CalendarInputProps extends InputProps {
  alignTo?: 'left' | 'right';
  calendarProps?: Omit<CalendarProps, 'ref' | 'dots' | 'interval' | 'onMonthChange' | 'selectedDay' | 'onDateSelected'>;
  date?: Date;
  flexProps?: FlexProps;
  onDateSelected(date: Date): void;
}

const defaultCalendarProps: CalendarInputProps['calendarProps'] = {
  fadeOtherMonths: true,
  disablePast: false,
  disableFuture: false,
  sx: {
    maxWidth: 360
  }
};

const CalendarInput = ({
  alignTo,
  calendarProps,
  // defaultProps no longer seem to be recognized...?
  date = new Date(),
  flexProps,
  onDateSelected,
  ...props
}: CalendarInputProps): JSX.Element => {
  const [showCalendar, setShowCalendar] = useState(false);
  const { interval, incDecMonth } = useDateIntervalFromMonth(date, true, 1);
  const calendarRef = useRef<HTMLDivElement>(null);

  useClickOutside(calendarRef, () => {
    setShowCalendar(false);
  });

  useKeydown(showCalendar, (event: KeyboardEvent): void => {
    if (['Escape', 'Tab'].includes(event.key)) {
      setShowCalendar(false);
    }
  });

  const mergedCalendarProps = Object.assign({}, defaultCalendarProps, calendarProps);

  return (
    <Flex {...flexProps} sx={{ position: 'relative', flexDirection: 'column', ...flexProps?.sx }}>
      <Input
        {...props}
        type="text"
        onFocus={(): void => setShowCalendar(true)}
        onClick={(): void => {
          if (!showCalendar) {
            setShowCalendar(true);
          }
        }}
      />
      {showCalendar && (
        <Calendar
          {...mergedCalendarProps}
          aria-modal
          dots={[]}
          interval={interval}
          onMonthChange={incDecMonth}
          onDateSelected={(selectedDate): void => {
            setShowCalendar(false);
            onDateSelected(selectedDate);
          }}
          ref={calendarRef}
          selectedDay={date}
          role="dialog"
          sx={{
            position: 'absolute',
            top: '100%',
            right: alignTo === 'right' ? 0 : 'initial',
            left: alignTo === 'left' ? 0 : 'initial',
            zIndex: 'focus',
            ...mergedCalendarProps?.sx
          }}
        />
      )}
    </Flex>
  );
};

CalendarInput.defaultProps = {
  alignTo: 'left',
  date: new Date(),
  readOnly: true
};

export default CalendarInput;
